import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Calculator, CalendarClock, ArrowRight } from "lucide-react";
import useSEO from "../../lib/useSEO";
import { SeoShell, SeoHero, PlanCTA } from "./SeoLayout";

export function LinkToThis({ path, name }) {
  const [done, setDone] = useState(false);
  const html = `<a href="https://capittance.com${path}">${name} by Capittance</a>`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(html); setDone(true); setTimeout(() => setDone(false), 2000); }
    catch { setDone(false); }
  };
  return (
    <div className="mt-10 p-5 rounded-md border bg-white" style={{ borderColor: "#F0D3D8" }} data-testid="link-to-this">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#7A1F2B" }}>Share this tool</div>
      <p className="text-sm text-zinc-700 mt-1">Free to use and share. Add it to your blog, wiki or resources page with this link:</p>
      <div className="mt-3 flex gap-2 items-stretch">
        <code className="flex-1 text-xs bg-zinc-50 border rounded-sm p-3 overflow-x-auto whitespace-nowrap" data-testid="link-to-this-code">{html}</code>
        <button onClick={copy} className="px-3 rounded-sm text-white inline-flex items-center gap-1.5 text-xs font-semibold" style={{ background: "#7A1F2B" }} data-testid="link-to-this-copy">
          {done ? <Check size={14}/> : <Copy size={14}/>} {done ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

export function ToolsHub() {
  useSEO({
    title: "Free ERP Go-Live Tools: Readiness Calculator & Cutover Timeline | Capittance",
    description: "Free tools for SAP, Oracle and Workday programmes: score your go-live readiness in two minutes and build a cutover timeline from your go-live date.",
    path: "/tools",
  });
  const tools = [
    { to: "/tools/go-live-readiness-calculator", icon: Calculator, name: "Go-live readiness calculator", body: "Answer 12 questions and get a readiness score, weak areas and the checks to close before your Go/No-Go." },
    { to: "/tools/cutover-timeline-builder", icon: CalendarClock, name: "Cutover timeline builder", body: "Enter your go-live date and get a dated cutover timeline from T-72h to hypercare exit. Download as CSV." },
  ];
  return (
    <SeoShell>
      <SeoHero eyebrow="Free tools · no sign-up" h1="Free tools for ERP go-lives." intro="Built from real cutover and testing playbooks. Use them, share them, link to them." />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-4" data-testid="tools-grid">
          {tools.map((t) => (
            <Link key={t.to} to={t.to} className="p-7 rounded-md bg-white border hover:-translate-y-0.5 transition-transform" style={{ borderColor: "#F0D3D8" }} data-testid={`tools-card-${t.to.split("/").pop()}`}>
              <span className="w-10 h-10 rounded-sm grid place-items-center text-white" style={{ background: "#7A1F2B" }}><t.icon size={18}/></span>
              <div className="font-display text-xl font-extrabold mt-3 text-[#0F1723]">{t.name}</div>
              <p className="text-sm text-zinc-600 mt-2">{t.body}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "#7A1F2B" }}>Open tool <ArrowRight size={13}/></span>
            </Link>
          ))}
        </div>
        <PlanCTA id="tools-hub" />
      </main>
    </SeoShell>
  );
}

const TRACK_WINDOWS = [
  ["Readiness Go/No-Go checkpoint 1", -72, 1, "Steering committee"],
  ["Legacy transaction freeze", -70, 2, "Legacy systems lead"],
  ["Final transport import & technical checks", -66, 10, "Basis lead"],
  ["Finance period-end in legacy", -54, 12, "Finance lead"],
  ["Data migration loads & reconciliation", -60, 30, "Data migration lead"],
  ["Security roles & access", -48, 8, "Security lead"],
  ["Interface switch-over & smoke tests", -36, 12, "Integration lead"],
  ["Data-loaded Go/No-Go checkpoint 2", -24, 1, "Executive sponsor"],
  ["Business verification scripts", -20, 16, "Process owners"],
  ["Final Go/No-Go checkpoint 3", -2, 1, "Executive sponsor"],
  ["Go-live: business opens in new ERP", 0, 1, "Cutover manager"],
  ["Hypercare war room (first 72h)", 0, 72, "Hypercare lead"],
  ["First month-end close", 24 * 30, 120, "Finance lead"],
  ["Hypercare exit review", 24 * 42, 2, "Executive sponsor"],
];

const fmt = (d) => d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export function CutoverTimelineBuilder() {
  useSEO({
    title: "Free Cutover Timeline Builder for SAP, Oracle & Workday | Capittance",
    description: "Enter your ERP go-live date and get a dated cutover timeline: legacy freeze, data loads, Go/No-Go checkpoints, go-live and hypercare exit. Free CSV download.",
    path: "/tools/cutover-timeline-builder",
    jsonLd: { "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Cutover Timeline Builder", applicationCategory: "BusinessApplication", operatingSystem: "Web", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } },
  });
  const [goLive, setGoLive] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7) + 28); d.setHours(6, 0, 0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const rows = useMemo(() => {
    const t0 = new Date(goLive);
    if (isNaN(t0)) return [];
    return TRACK_WINDOWS.map(([name, off, dur, owner]) => {
      const s = new Date(t0.getTime() + off * 3600e3);
      return { name, owner, start: s, end: new Date(s.getTime() + dur * 3600e3), off };
    }).sort((a, b) => a.start - b.start);
  }, [goLive]);
  const csv = () => {
    const lines = [["Activity", "Owner", "Start", "End", "Offset (h)"], ...rows.map((r) => [r.name, r.owner, r.start.toISOString(), r.end.toISOString(), r.off])];
    const blob = new Blob([lines.map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "capittance-cutover-timeline.csv"; a.click(); URL.revokeObjectURL(a.href);
  };
  return (
    <SeoShell>
      <SeoHero eyebrow="Free tool" h1="Cutover timeline builder" intro="Pick your go-live date and time. Get every major cutover milestone, owner and Go/No-Go checkpoint with real dates." />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm font-semibold text-[#0F1723]">Go-live (T-0)
            <input type="datetime-local" value={goLive} onChange={(e) => setGoLive(e.target.value)} className="block mt-1 border rounded-sm px-3 py-2 bg-white" style={{ borderColor: "#E5C7CC" }} data-testid="timeline-golive-input"/>
          </label>
          <button onClick={csv} disabled={!rows.length} className="px-4 py-2.5 rounded-sm text-white text-sm font-semibold" style={{ background: "#7A1F2B" }} data-testid="timeline-download-csv">Download CSV</button>
        </div>
        <div className="mt-6 rounded-md border bg-white overflow-x-auto" style={{ borderColor: "#F0D3D8" }}>
          <table className="w-full text-sm min-w-[640px]" data-testid="timeline-table">
            <thead><tr className="text-left text-white" style={{ background: "#4A0F1A" }}>{["Activity", "Owner", "Start", "End"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={r.name} className="border-t" style={{ borderColor: "#F0D3D8", background: r.name.includes("Go/No-Go") || r.off === 0 ? "#FFF7E6" : i % 2 ? "#FDFBFB" : "#FFF" }} data-testid={`timeline-row-${i}`}>
                <td className="p-3 font-medium">{r.name}</td><td className="p-3 text-zinc-600">{r.owner}</td><td className="p-3">{fmt(r.start)}</td><td className="p-3">{fmt(r.end)}</td>
              </tr>))}</tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-zinc-600">Need every task, not just milestones? <a href="/downloads/guides/Capittance-S4HANA-Cutover-Runbook-214.xlsx" download className="font-semibold underline" style={{ color: "#7A1F2B" }} data-testid="timeline-runbook-download">Download the free 214-step runbook</a>.</p>
        <LinkToThis path="/tools/cutover-timeline-builder" name="Cutover Timeline Builder" />
        <PlanCTA id="timeline" />
      </main>
    </SeoShell>
  );
}
